'use strict';

import {
    FilterDescriptor,
    FilterMatchMode,
    FilterValueType
} from '~/shared/src/validationSchemes/modules/overview';


export type CustomFilter = {
    value : string;
    matchMode : FilterMatchMode;
};

export type CustomFilterMode = 'AND' | 'OR';

/**
 * Checks if the filterDescription is an inverted one.
 *
 * @param {FilterDescriptor} filterDescription
 *
 * @return {Boolean}
 */
export const isInvertedStringFilter = ( filterDescription : FilterDescriptor & object ) : boolean => {
    let retVal = false;

    switch ( filterDescription.operator ) {
        case 'notContains':
        case 'notEndsWith':
        case 'notStartsWith':
        case 'notIn':
            retVal = true;
            break;
    }

    return retVal;
}

/**
 * Gets the mode of custom filter for value
 *
 * @param {any} value
 *
 * @return {CustomFilterMode}
 */
export const getCustomFilterMode = ( value : any ) : CustomFilterMode => {
    let retVal : CustomFilterMode = 'AND';

    if ( value ) {
        value = String( value );

        if ( value && value.indexOf( '||' ) !== -1 ) {
            retVal = 'OR';
        }
    }
    return retVal;
}

/**
 * Gets the default matchMode for valueType
 *
 * @param {FilterValueType} valueType
 *
 * @return {FilterMatchMode}
 */
export const getDefaultFilterMatchMode = ( valueType : FilterValueType ) : FilterMatchMode => {
    return [ 'COLOR', 'COORDINATE', 'EMAIL', 'IBAN', 'IMAGEDATA', 'JSON', 'LINK', 'PHONE', 'STATUSNAME', 'STRING', 'TEXT', 'TIMERULE' ].indexOf( valueType ) !== -1
        ? 'contains'
        : 'equals';
}

/**
 * Gets the matchMode for value
 *
 * @param {any} value
 *
 * @return {FilterMatchMode}
 */
export const getCustomFilterMatchMode = ( value : any ) : FilterMatchMode | undefined => {
    let retVal : FilterMatchMode | undefined;

    // null search operators
    if ( /^(is )?(not )?(empty|null)$/i.test( value ) ) {
        if ( value.toLowerCase().indexOf( 'not' ) !== -1 ) {
            retVal = 'notEmpty';
        }
        else {
            retVal = 'empty';
        }
    }

    // other search operators
    else if ( /^((<|<=|!=|<>|>|>=|=) ).*$/i.test( value ) ) {
        switch ( value.substring( 0, value.indexOf( ' ' ) ) ) {
            case '<':
                retVal = 'lt';
                break;

            case '<=':
                retVal = 'lte';
                break;

            case '!=':
            case '<>':
                retVal = 'notEquals';
                break;

            case '=':
                retVal = 'equals';
                break;

            case '>':
                retVal = 'gt';
                break;

            case '>=':
                retVal = 'gte';
                break;
        }
    }

    else if ( value ) {

        let not = false;
        if ( /^! /i.test( value ) ) {
            not = true;
            value = value.substring( value.indexOf( ' ' ) ).trim();
        }

        const wildcardFirst = /^[%*]/.test( value ),
              wildcardLast  = /[%*]$/.test( value );

        // no wildcard or wildcard first and last
        if ( wildcardFirst === wildcardLast ) {
            if ( not ) {
                retVal = 'notContains';
            }
            else {
                retVal = 'contains';
            }
        }
        else if ( wildcardFirst ) {
            if ( not ) {
                retVal = 'notEndsWith';
            }
            else {
                retVal = 'endsWith';
            }
        }
        else if ( wildcardLast ) {
            if ( not ) {
                retVal = 'notStartsWith';
            }
            else {
                retVal = 'startsWith';
            }
        }
    }

    return retVal;
}

/**
 * Gets the filter values for value
 *
 * @param {any} value
 *
 * @return {string}
 */
export const getCustomFilterValues = ( value : any ) : string[] => {
    return String( value ).split( /\|\||&&/ ).map( v => v.trim() );
}

/**
 * Gets the filter values for value
 *
 * @param {any} value
 * @param {FilterValueType} valueType
 *
 * @return {string}
 */
export const getCustomFilters = ( value : any, valueType : FilterValueType ) : CustomFilter[] => {
    const retVal : CustomFilter[] = [],
          customFilterValues      = String( value ?? '' ).split( /\|\||&&/ ).map( v => v.trim() );

    for ( const customFilterValue of customFilterValues ) {
        retVal.push( {
            value    : customFilterValue
                ? getCustomFilterValue( customFilterValue )
                : '',
            matchMode: customFilterValue
                ? getCustomFilterMatchMode( customFilterValue ) ?? getDefaultFilterMatchMode( valueType )
                : getDefaultFilterMatchMode( valueType )
        } );
    }

    return retVal;
}

export const getValue = ( customFilters : CustomFilter[], customFilterMode : CustomFilterMode ) : string => {
    const customFilterStrings : string[] = [];

    for ( const customFilter of customFilters ) {
        const customFilterString = getValueStringFromMatchModeAndValue( customFilter.matchMode, customFilter.value );

        if ( customFilterString ) {
            customFilterStrings.push( customFilterString );
        }
    }

    return customFilterStrings.join( customFilterMode === 'OR' ? ' || ' : ' && ' );
}

/**
 * Gets the filter value for value
 *
 * @param {any} value
 *
 * @return {string}
 */
export const getCustomFilterValue = ( value : any ) : string => {
    let retVal : string = String( value );

    // null search operators
    if ( /^(is )?(not )?(empty|null)$/i.test( retVal ) ) {
        retVal = '';
    }

    // other search operators
    else if ( /^((<|<=|!=|<>|>|>=|=) ).*$/i.test( retVal ) ) {
        retVal = retVal.substring( retVal.indexOf( ' ' ) );
    }

    else if ( retVal ) {
        if ( /^! /i.test( retVal ) ) {
            retVal = retVal.substring( retVal.indexOf( ' ' ) ).trim();
        }

        retVal = retVal.replace( /[*%]/g, '' );
    }

    retVal = retVal.trim();

    return retVal;
}

export const getValueStringFromMatchModeAndValue = ( matchMode : FilterMatchMode, value : any ) : string => {
    let retVal : string = '';

    if ( ( value !== undefined && value !== null && value !== '' ) || matchMode === 'empty' || matchMode === 'notEmpty' ) {
        switch ( matchMode ) {
            case 'empty':
                retVal = 'empty';
                break;

            case 'notEmpty':
                retVal = 'not empty';
                break;

            case 'lt':
                retVal = `< ${value}`;
                break;

            case 'lte':
                retVal = `<= ${value}`;
                break;

            case 'equals':
                retVal = `= ${value}`;
                break;

            case 'notEquals':
                retVal = `<> ${value}`;
                break;

            case 'gt':
                retVal = `> ${value}`;
                break;

            case 'gte':
                retVal = `>= ${value}`;
                break;

            case 'startsWith':
                retVal = `${value}*`;
                break;

            case 'notStartsWith':
                retVal = `! ${value}*`;
                break;

            case 'contains':
                retVal = `*${value}*`;
                break;

            case 'notContains':
                retVal = `! *${value}*`;
                break;

            case 'endsWith':
                retVal = `*${value}`;
                break;

            case 'notEndsWith':
                retVal = `! *${value}`;
                break;
        }

        retVal = retVal.trim();
    }

    return retVal;
}
